import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, CARD_WIDTH, CARD_HEIGHT } from '@hokm/shared';
import { RENDER_SCALE } from '../renderScale';

interface FlyingCard {
  sprite: Phaser.GameObjects.Image;
  startX: number;
  startY: number;
  startRotation: number;
}

/**
 * Creates dramatic card-based scene transitions.
 * Cards fly in from edges to form a deck, or scatter outward.
 */
export class CardTransition {
  private scene: Phaser.Scene;
  private cards: FlyingCard[] = [];
  private container: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setDepth(100); // Above everything
  }

  /**
   * Cards fly in from screen edges and converge to center, forming a deck.
   * Use when transitioning TO the game scene.
   */
  flyIn(onComplete?: () => void): void {
    const cardCount = 12;
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2 - 30;

    // Create cards at random edge positions
    for (let i = 0; i < cardCount; i++) {
      const card = this.createCardAtEdge(i, cardCount);
      this.cards.push(card);
    }

    // Animate each card to center with stagger
    this.cards.forEach((card, index) => {
      const delay = index * 50;
      const targetY = centerY - index * 0.5; // Stack offset
      const targetX = centerX + index * 0.3;

      this.scene.tweens.add({
        targets: card.sprite,
        x: targetX,
        y: targetY,
        rotation: 0,
        scaleX: 1 / RENDER_SCALE,
        scaleY: 1 / RENDER_SCALE,
        duration: 500,
        delay,
        ease: 'Back.easeOut',
        onComplete:
          index === this.cards.length - 1
            ? () => {
                // Small deck formation animation on last card
                this.scene.time.delayedCall(200, () => {
                  this.cleanup();
                  if (onComplete) onComplete();
                });
              }
            : undefined,
      });
    });
  }

  /**
   * Cards scatter from center to screen edges with fade.
   * Use when transitioning FROM the game scene.
   */
  flyOut(onComplete?: () => void): void {
    const cardCount = 12;
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2 - 30;

    // Create cards stacked at center
    for (let i = 0; i < cardCount; i++) {
      const sprite = this.scene.add.image(centerX + i * 0.3, centerY - i * 0.5, 'card-back');
      sprite.setScale(1 / RENDER_SCALE);
      sprite.setRotation(0);
      this.container.add(sprite);
      this.cards.push({
        sprite,
        startX: centerX,
        startY: centerY,
        startRotation: 0,
      });
    }

    // Scatter each card to edges with different trajectories
    this.cards.forEach((card, index) => {
      const delay = index * 30;
      const angle = (index / this.cards.length) * Math.PI * 2 + Math.random() * 0.5;
      const distance = 600 + Math.random() * 200;
      const targetX = centerX + Math.cos(angle) * distance;
      const targetY = centerY + Math.sin(angle) * distance;
      const targetRotation = (Math.random() - 0.5) * Math.PI * 2;

      this.scene.tweens.add({
        targets: card.sprite,
        x: targetX,
        y: targetY,
        rotation: targetRotation,
        alpha: 0,
        scaleX: 0.3 / RENDER_SCALE,
        scaleY: 0.3 / RENDER_SCALE,
        duration: 600,
        delay,
        ease: 'Quad.easeIn',
        onComplete:
          index === this.cards.length - 1
            ? () => {
                this.cleanup();
                if (onComplete) onComplete();
              }
            : undefined,
      });
    });
  }

  /**
   * Create a card at a random position outside the screen edges.
   */
  private createCardAtEdge(index: number, total: number): FlyingCard {
    // Distribute cards around all 4 edges
    const edge = index % 4;
    let startX: number;
    let startY: number;
    let startRotation: number;

    const margin = 100;
    const progress = index / total;

    switch (edge) {
      case 0: // Top
        startX = GAME_WIDTH * progress;
        startY = -CARD_HEIGHT - margin;
        startRotation = Math.PI + Math.random() * 0.5;
        break;
      case 1: // Right
        startX = GAME_WIDTH + CARD_WIDTH + margin;
        startY = GAME_HEIGHT * progress;
        startRotation = -Math.PI / 2 + Math.random() * 0.5;
        break;
      case 2: // Bottom
        startX = GAME_WIDTH * (1 - progress);
        startY = GAME_HEIGHT + CARD_HEIGHT + margin;
        startRotation = Math.random() * 0.5;
        break;
      case 3: // Left
      default:
        startX = -CARD_WIDTH - margin;
        startY = GAME_HEIGHT * (1 - progress);
        startRotation = Math.PI / 2 + Math.random() * 0.5;
        break;
    }

    const sprite = this.scene.add.image(startX, startY, 'card-back');
    sprite.setScale(0.5 / RENDER_SCALE);
    sprite.setRotation(startRotation);
    this.container.add(sprite);

    return { sprite, startX, startY, startRotation };
  }

  /**
   * Cards fly in from screen edges, converge to center, then fade out.
   * Used for loading screen transition - cards reveal lobby behind them.
   */
  flyInAndFadeOut(onConverged?: () => void, onComplete?: () => void): void {
    const cardCount = 16;
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2 - 30;

    // Create cards at random edge positions
    for (let i = 0; i < cardCount; i++) {
      const card = this.createCardAtEdge(i, cardCount);
      this.cards.push(card);
    }

    // Animate each card to center with stagger
    let converged = 0;
    this.cards.forEach((card, index) => {
      const delay = index * 30;
      const targetY = centerY - index * 0.5; // Stack offset
      const targetX = centerX + index * 0.3;

      this.scene.tweens.add({
        targets: card.sprite,
        x: targetX,
        y: targetY,
        rotation: 0,
        scaleX: 1 / RENDER_SCALE,
        scaleY: 1 / RENDER_SCALE,
        duration: 500,
        delay,
        ease: 'Back.easeOut',
        onComplete: () => {
          converged++;
          if (converged === this.cards.length) {
            onConverged?.();
            // Brief hold, then fade out
            this.scene.time.delayedCall(200, () => {
              this.fadeOutCards(onComplete);
            });
          }
        },
      });
    });
  }

  /**
   * Fade out all cards with slight scale down.
   */
  private fadeOutCards(onComplete?: () => void): void {
    let faded = 0;
    this.cards.forEach((card, index) => {
      this.scene.tweens.add({
        targets: card.sprite,
        alpha: 0,
        scaleX: 0.8 / RENDER_SCALE,
        scaleY: 0.8 / RENDER_SCALE,
        duration: 300,
        delay: index * 15,
        ease: 'Quad.easeIn',
        onComplete: () => {
          faded++;
          if (faded === this.cards.length) {
            this.cleanup();
            onComplete?.();
          }
        },
      });
    });
  }

  /**
   * Cards scatter from center partway toward edges, then pause.
   * Used for lobby→game transition - first phase before scene switch.
   */
  scatterPartial(onMidPoint?: () => void): void {
    const cardCount = 13; // Full hand of cards
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2 - 30;

    // Create cards stacked at center
    for (let i = 0; i < cardCount; i++) {
      const sprite = this.scene.add.image(centerX + i * 0.3, centerY - i * 0.5, 'card-back');
      sprite.setScale(1 / RENDER_SCALE);
      sprite.setRotation(0);
      this.container.add(sprite);
      this.cards.push({
        sprite,
        startX: centerX,
        startY: centerY,
        startRotation: 0,
      });
    }

    // Scatter each card partway (40% of full distance)
    let completed = 0;
    this.cards.forEach((card, index) => {
      const delay = index * 20;
      const angle = (index / this.cards.length) * Math.PI * 2 + Math.random() * 0.3;
      const partialDistance = (300 + Math.random() * 100) * 0.4;
      const targetX = centerX + Math.cos(angle) * partialDistance;
      const targetY = centerY + Math.sin(angle) * partialDistance;
      const targetRotation = (Math.random() - 0.5) * Math.PI * 0.5;

      this.scene.tweens.add({
        targets: card.sprite,
        x: targetX,
        y: targetY,
        rotation: targetRotation,
        scaleX: 0.7 / RENDER_SCALE,
        scaleY: 0.7 / RENDER_SCALE,
        duration: 400,
        delay,
        ease: 'Quad.easeOut',
        onComplete: () => {
          completed++;
          if (completed === this.cards.length) {
            onMidPoint?.();
          }
        },
      });
    });
  }

  /**
   * Continue cards from current positions to target hand positions.
   * Used after scene switch - cards animate into the player's hand.
   */
  continueToPositions(positions: Array<{ x: number; y: number; rotation: number }>, onComplete?: () => void): void {
    // Use the first N positions for our cards
    const targetPositions = positions.slice(0, this.cards.length);

    let completed = 0;
    this.cards.forEach((card, index) => {
      const target = targetPositions[index] || positions[positions.length - 1];
      const delay = index * 30;

      this.scene.tweens.add({
        targets: card.sprite,
        x: target.x,
        y: target.y,
        rotation: target.rotation,
        scaleX: 1 / RENDER_SCALE,
        scaleY: 1 / RENDER_SCALE,
        duration: 500,
        delay,
        ease: 'Back.easeOut',
        onComplete: () => {
          completed++;
          if (completed === this.cards.length) {
            onComplete?.();
          }
        },
      });
    });
  }

  /**
   * Flip cards to reveal their faces (swap texture with flip animation).
   */
  revealCards(cardKeys: string[], onComplete?: () => void): void {
    let completed = 0;
    const totalCards = Math.min(this.cards.length, cardKeys.length);

    this.cards.forEach((card, index) => {
      if (index >= cardKeys.length) return;

      const delay = index * 50;
      const cardKey = cardKeys[index];

      // First half: scale X to 0 (card flipping)
      this.scene.tweens.add({
        targets: card.sprite,
        scaleX: 0,
        duration: 150,
        delay,
        ease: 'Quad.easeIn',
        onComplete: () => {
          // Swap texture at midpoint
          card.sprite.setTexture(cardKey);

          // Second half: scale X back to normal
          this.scene.tweens.add({
            targets: card.sprite,
            scaleX: 1 / RENDER_SCALE,
            duration: 150,
            ease: 'Quad.easeOut',
            onComplete: () => {
              completed++;
              if (completed === totalCards) {
                onComplete?.();
              }
            },
          });
        },
      });
    });
  }

  /**
   * Get the card sprites for external manipulation.
   */
  getCards(): FlyingCard[] {
    return this.cards;
  }

  /**
   * Get the container (useful for scene transitions).
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Transfer ownership of cards to a new scene.
   * Returns the container which should be added to the new scene.
   */
  transferToScene(newScene: Phaser.Scene): void {
    this.scene = newScene;
    // Container remains in memory - the overlay scene manages it
  }

  /**
   * Clean up all card sprites.
   */
  cleanup(): void {
    this.cards.forEach((card) => card.sprite.destroy());
    this.cards = [];
  }

  /**
   * Destroy the transition and all resources.
   */
  destroy(): void {
    this.cleanup();
    this.container.destroy();
  }
}
